import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, AlertTriangle } from "lucide-react";
import { api, errCode, download } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useMaster, nameOf } from "../lib/master";
import { fmtDateTime, todayCL } from "../lib/time";
import { PageTitle, Sel, Inp } from "../components/Common";
import { ReleaseBadge } from "../components/Badges";
import { ResidueAlerts } from "./LabResults";

const CsgDocs = ({ csg }) => {
  const { t, te, lang } = useLang();
  const [docs, setDocs] = useState([]);
  const load = () => api.get(`/csgs/${csg.id}/documents`).then((r) => setDocs(r.data));
  useEffect(() => { load(); }, [csg.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/csgs/${csg.id}/documents`, fd); toast.success(t("saved")); load(); } catch (ex) { toast.error(te(errCode(ex))); }
  };
  return (
    <div className="p-3 border border-slate-200 rounded-xl" data-testid={`csg-${csg.code}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono font-bold">{csg.code}</span>
        <label className="btn-secondary btn-sm cursor-pointer"><Upload size={14} />{t("upload")}
          <input type="file" className="hidden" onChange={upload} data-testid={`csg-upload-${csg.code}`} /></label>
      </div>
      <ul className="mt-2 space-y-1">{docs.map((d) => (
        <li key={d.id}><button className="text-sm underline flex items-center gap-1" onClick={() => download(`/csg-documents/${d.id}/file`, d.filename)}>
          <FileText size={14} />{d.filename}</button><span className="text-xs text-slate-500">{d.uploaded_by} · {fmtDateTime(d.created_at, lang)}</span></li>))}
        {!docs.length && <li className="text-xs text-slate-500">{t("none")}</li>}</ul>
    </div>
  );
};

export default function Releases() {
  const { t, te, lang } = useLang();
  const farms = useMaster("farms");
  const csgs = useMaster("csgs");
  const sectors = useMaster("sectors");
  const varieties = useMaster("varieties");
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ sector_id: "", variety_id: "", status: "released", valid_from: todayCL(), valid_to: todayCL(), reason: "" });
  const [alerts, setAlerts] = useState([]);
  const load = () => api.get("/releases").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/lab-results/alerts").then((r) => setAlerts(r.data)).catch(() => {}); }, []);
  const sec = sectors.find((s) => s.id === f.sector_id);
  const residueFail = sec && alerts.some((a) => a.csg_id === sec.csg_id && (!a.sector_id || a.sector_id === sec.id) && (!a.variety_id || !f.variety_id || a.variety_id === f.variety_id));
  const save = () => api.post("/releases", f).then(() => { toast.success(t("saved")); setF({ ...f, reason: "" }); load(); }).catch((e) => toast.error(te(errCode(e))));
  const secOpts = sectors.map((s) => ({ id: s.id, name: `${nameOf(farms, s.farm_id)} · ${s.name} (${s.code})` }));

  return (
    <div className="space-y-5" data-testid="releases-page">
      <PageTitle>{t("releases_title")}</PageTitle>
      <ResidueAlerts alerts={alerts} />
      <section className="card p-4 space-y-3">
        <h2 className="font-bold text-lg">{t("documents")}</h2>
        <div className="grid md:grid-cols-3 gap-3">{csgs.map((c) => <CsgDocs key={c.id} csg={c} />)}</div>
      </section>
      <section className="card p-4 space-y-3" data-testid="release-form">
        <h2 className="font-bold text-lg">{t("new_release")}</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          <Sel label={t("sector")} value={f.sector_id} onChange={(v) => setF({ ...f, sector_id: v })} options={secOpts} testid="release-sector-select" placeholder={t("select")} />
          <Sel label={t("variety")} value={f.variety_id} onChange={(v) => setF({ ...f, variety_id: v })} options={varieties} testid="release-variety-select" placeholder={t("select")} />
          <label><span className="eyebrow">{t("status")}</span>
            <select className="field mt-1" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="release-status-select">
              <option value="released">{t("release_released")}</option><option value="hold">{t("release_hold")}</option></select></label>
          <Inp label={t("valid_from")} type="date" value={f.valid_from} onChange={(e) => setF({ ...f, valid_from: e.target.value })} testid="release-from-input" />
          <Inp label={t("valid_to")} type="date" value={f.valid_to} onChange={(e) => setF({ ...f, valid_to: e.target.value })} testid="release-to-input" />
          <Inp label={t("reason")} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} testid="release-reason-input" />
        </div>
        {residueFail && f.status === "released" && <p className="flex items-center gap-2 text-sm font-bold text-red-800 bg-red-50 border border-red-300 rounded-lg p-2" data-testid="release-residue-warning"><AlertTriangle size={16} />{t("residue_release_warning")}</p>}
        <button className="btn-primary" disabled={!f.sector_id || !f.variety_id || f.reason.length < 2} onClick={save} data-testid="release-save-button">{t("save")}</button>
      </section>
      <div className="card overflow-x-auto">
        <table className="tbl w-full" data-testid="releases-table">
          <thead><tr><th>{t("sector")}</th><th>{t("variety")}</th><th>{t("status")}</th><th>{t("valid_from")}–{t("valid_to")}</th><th>{t("reason")}</th><th>{t("approved_by")}</th></tr></thead>
          <tbody>{rows.slice(0, 200).map((r) => (
            <tr key={r.id}><td>{secOpts.find((s) => s.id === r.sector_id)?.name}</td><td>{nameOf(varieties, r.variety_id)}</td><td><ReleaseBadge status={r.status} /></td>
              <td className="font-mono text-xs">{r.valid_from} → {r.valid_to}</td><td className="text-sm">{r.reason}</td>
              <td className="text-xs">{r.approved_by}<br />{fmtDateTime(r.created_at, lang)}</td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}
